<template>
  <div class="container">

    <h1 class="se-bp-title">Select Wage Documentation: {{ getWageEmploymentLabel(employmentIdx) }}</h1>

    <form autocomplete="off" v-if="currentEmployment">

      <!-- ================= Document Type Selection ================= -->
      <p>Select the loan documentation for the borrower’s current employment. <br>A year-to-date paystub is required, along with either the prior year-end paystub or prior-year W-2. You may provide both.</p>

      <label class="bp-form-label d-block mt-4 mb-3">Select all that apply:</label>
      <div class="se-doc-checkbox-list">
        <div class="se-doc-checkbox-row" v-for="opt in documentTypeOptions" :key="opt.value">
          <el-checkbox :model-value="isDocumentTypeSelected(opt.value)" :disabled="isLocked || opt.disabled"
                       @change="toggleDocumentType(opt.value)">{{ opt.label }}</el-checkbox>
          <a class="btn-link se-help-icon" v-tooltip.top="'Learn More'" @click="opt.showHelp = true"><i class="fa fa-info-circle"></i></a>
        </div>
      </div>

    </form>

    <se-buttons
      :display-previous="true"
      :display-save="false"
      :display-submit="false"
      @seNext="next"
      @sePrevious="previous">
    </se-buttons>

    <se-help v-for="opt in documentTypeOptions" :key="'help-'+opt.value" v-model:visible="opt.showHelp" title="Learn More">
      <p>{{ opt.helpText }}</p>
    </se-help>

  </div>
</template>
<script>
import { defineComponent } from 'vue';
import {ElCheckbox} from 'element-plus'
import Swal from 'sweetalert2/dist/sweetalert2'
import SeButtons from './SeButtons.vue'
import SeHelp from './SeHelp.vue'
import wageFormMixin from '@/mixins/WageFormMixin'
import Enums from 'src/model/enums'
import {
  ADD_WAGE_INCOME_DOCUMENT, DELETE_WAGE_INCOME_DOCUMENT, SET_WAGE_INCOME_DOCUMENT_DATA
} from '@/store/actions/se'

export default defineComponent({
  mixins: [wageFormMixin],

  components: {
    SeButtons,
    SeHelp,
    [ElCheckbox.name]: ElCheckbox
  },

  data() {
    return {
      documentTypeOptions: [
        {
          value: Enums.SeWageDocumentationType.yearToDatePayStub,
          label: 'Year-to-date paystub',
          helpText: 'A recent paystub showing the borrower\'s year-to-date earnings from this employer.',
          showHelp: false
        },
        {
          value: Enums.SeWageDocumentationType.yearEndPayStub,
          label: 'Prior year-end paystub (most effective when bonus, commission, overtime, tips are calculated)',
          helpText: 'The borrower\'s final paystub for a prior calendar year, showing total earnings for that year.',
          showHelp: false
        },
        {
          value: Enums.SeWageDocumentationType.w2Form,
          label: 'W-2 Form',
          helpText: 'The borrower\'s W-2 form(s) reporting wages and tax withholding for this employer.',
          showHelp: false
        }
        // ,
        // {
        //   value: 'voe1005',
        //   label: 'Verification of Employment (Form 1005)',
        //   helpText: 'Income Calculator does not currently support Verification of Employment (Form 1005). This option will be available in a future release.',
        //   disabled: true,
        //   showHelp: false
        // }
      ]
    }
  },

  methods: {
    next() {
      let hasYtd = this.isDocumentTypeSelected(Enums.SeWageDocumentationType.yearToDatePayStub)
      let hasPriorYear = this.isDocumentTypeSelected(Enums.SeWageDocumentationType.yearEndPayStub)
        || this.isDocumentTypeSelected(Enums.SeWageDocumentationType.w2Form)
      if (!hasYtd || !hasPriorYear) {
        Swal.fire({
          html: '<p>A year-to-date paystub is required, along with either the prior year-end paystub or prior-year W-2</p>',
          width: 480,
          buttonsStyling: false,
          customClass: {confirmButton: 'bp-button bp-button--primary'},
          confirmButtonText: 'OK',
          icon: 'warning'
        })
        return
      }
      wageFormMixin.methods.next.call(this)
    },

    isDocumentTypeSelected(docType) {
      return (this.currentEmployment?.incomeDocuments || []).some(doc => doc.wageDocumentation === docType)
    },

    toggleDocumentType(docType) {
      let existingIdx = (this.currentEmployment?.incomeDocuments || []).findIndex(doc => doc.wageDocumentation === docType)
      if (existingIdx !== -1) {
        this.confirmDelete('This income document, including all income items, will be permanently removed.', () => {
          this.$store.commit(DELETE_WAGE_INCOME_DOCUMENT, {employmentIdx: this.employmentIdx, docIdx: existingIdx})
        })
      } else {
        this.$store.commit(ADD_WAGE_INCOME_DOCUMENT, {employmentIdx: this.employmentIdx})
        let newIdx = this.currentEmployment.incomeDocuments.length - 1
        this.$store.commit(SET_WAGE_INCOME_DOCUMENT_DATA, {employmentIdx: this.employmentIdx, docIdx: newIdx, fields: {wageDocumentation: docType}})
        this.saveRequest()
      }
    }
  },
});
</script>
<style scoped>

.se-doc-checkbox-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 24px;
}

.se-doc-checkbox-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

:deep(.el-checkbox) {
  margin-bottom: 0;
  height: auto;
}

:deep(.el-checkbox__label) {
  font-size: 1rem;
}

/* Element Plus checkboxes default to a light blue (#409eff) when checked.
   Use the same blue as the "Add Pay Rate" / "+ Add Paystub/W2" buttons
   (.bp-button--primary, #0a649d) for visual consistency. */
:deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  background-color: #0a649d;
  border-color: #0a649d;
}

:deep(.el-checkbox__input.is-checked + .el-checkbox__label) {
  color: #0a649d;
}

:deep(.el-checkbox__input.is-focus .el-checkbox__inner) {
  border-color: #0a649d;
}

:deep(.el-checkbox__input .el-checkbox__inner:hover) {
  border-color: #0a649d;
}
</style>


